import type {
  EditorEngineContract,
  EditorSaveStatus,
} from '@bangle.io/context';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

type SaveStatusSource = Pick<
  EditorEngineContract,
  'getSaveStatus' | 'listFailedSaves' | 'subscribeToSaveStatus'
>;

const CLEAN_STATUS: EditorSaveStatus = { status: 'clean' };

/** Hold "Saving…" briefly after the queue goes clean so the pill does not blink. */
export const SAVE_STATUS_CLEAN_HYSTERESIS_MS = 400;

/**
 * Subscribes to the engine save queue for one note. Status lives outside
 * Jotai, so this must use an external store — otherwise a write that settles
 * after the file tree dropped the path would never re-render the indicator.
 *
 * Clean transitions are delayed slightly so rapid pending↔clean flips during
 * typing do not flicker the Saved / Saving chrome.
 */
export function useEditorSaveStatus(
  saveStatus: SaveStatusSource,
  wsPath: string | undefined,
): EditorSaveStatus {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      wsPath === undefined
        ? () => {}
        : saveStatus.subscribeToSaveStatus(onStoreChange, wsPath),
    [saveStatus, wsPath],
  );

  // Discriminant only: `getSaveStatus` may return a fresh object each call
  // for the clean default, which would loop useSyncExternalStore.
  const kind = useSyncExternalStore(subscribe, () =>
    wsPath === undefined ? 'clean' : saveStatus.getSaveStatus(wsPath).status,
  );

  const liveStatus: EditorSaveStatus =
    wsPath === undefined || kind === 'clean'
      ? CLEAN_STATUS
      : saveStatus.getSaveStatus(wsPath);

  const [displayStatus, setDisplayStatus] =
    useState<EditorSaveStatus>(liveStatus);
  const heldDirtyRef = useRef<EditorSaveStatus | null>(null);

  useEffect(() => {
    if (liveStatus.status !== 'clean') {
      heldDirtyRef.current = liveStatus;
      setDisplayStatus(liveStatus);
      return;
    }

    if (!heldDirtyRef.current) {
      setDisplayStatus(CLEAN_STATUS);
      return;
    }

    const timer = setTimeout(() => {
      heldDirtyRef.current = null;
      setDisplayStatus(CLEAN_STATUS);
    }, SAVE_STATUS_CLEAN_HYSTERESIS_MS);

    return () => clearTimeout(timer);
  }, [liveStatus]);

  return displayStatus;
}

/**
 * Subscribes to any failed durable write in this tab so a sticky banner can
 * stay visible until every failure is retried successfully.
 */
export function useFailedEditorSaves(saveStatus: SaveStatusSource) {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      saveStatus.subscribeToSaveStatus(onStoreChange),
    [saveStatus],
  );

  const failedSignature = useSyncExternalStore(subscribe, () => {
    const failed = saveStatus.listFailedSaves();
    if (failed.length === 0) {
      return '';
    }
    return failed.map((entry) => entry.wsPath).join('\0');
  });

  if (failedSignature.length === 0) {
    return [];
  }

  return saveStatus.listFailedSaves();
}
